/**
 * @file Camera view — photo capture, preview, attach to jobs
 */

import { capturePhoto, compressPhoto, uploadPhoto } from '@services/photos.js';
import { getJobs, getJobById, addJobPhoto } from '@services/jobs.js';
import { load, store } from '@state/store.js';
import { toast } from '@components/toast.js';
import { showModal, closeModal } from '@components/modal.js';

let _selectedJobId = '';

function render() {
  const jobs = getJobs().filter(j =>
    j.stage !== 'Paid & Closed' &&
    j.stage !== 'Proposal Rejected'
  );
  const recentPhotos = getRecentPhotos();
  const selectedJob = _selectedJobId ? getJobById(_selectedJobId) : null;
  const photoCount = selectedJob?.photos ? selectedJob.photos.length : 0;

  return `
    <div class="view-header">
      <h1>Camera</h1>
    </div>

    <div class="card mb-md">
      <div class="form-group">
        <label>Attach to Job</label>
        <select id="camera-job-select">
          <option value="">Select a job...</option>
          ${jobs.map(j => {
            const jobPhotos = j.photos ? j.photos.length : 0;
            return `<option value="${esc(j.id)}" ${j.id === _selectedJobId ? 'selected' : ''}>${esc(j.desc || j.name || 'Job')} (${esc(j.stage)})${jobPhotos > 0 ? ` [${jobPhotos} photos]` : ''}</option>`;
          }).join('')}
        </select>
      </div>
      ${_selectedJobId && photoCount > 0 ? `
        <div class="text-muted text-sm" style="margin-top:-4px;margin-bottom:8px;">
          ${photoCount} photo${photoCount !== 1 ? 's' : ''} attached to this job
        </div>
      ` : ''}
    </div>

    <div class="text-center" style="padding:16px 0 24px;">
      <button class="capture-btn-large" id="capture-btn">
        <span class="capture-btn-icon">\uD83D\uDCF7</span>
      </button>
      <div class="text-muted text-sm mt-sm">Tap to capture photo</div>
    </div>

    <input type="file" accept="image/*" capture="environment" id="camera-file-input" style="display:none;" />

    <div class="flex-between mb-sm">
      <h3 style="color:var(--gold);font-size:0.85rem;text-transform:uppercase;">Recent Photos</h3>
      <span class="text-muted text-sm">${recentPhotos.length} photos</span>
    </div>
    ${recentPhotos.length > 0
      ? `<div class="photo-grid" id="recent-photos">${recentPhotos.map(p =>
          `<div class="photo-grid-item" data-photo-url="${esc(typeof p.url === 'string' ? p.url : '')}" data-job-id="${esc(p.jobId || '')}">
            <img src="${esc(typeof p.url === 'string' ? p.url : '')}" alt="Photo" loading="lazy" />
            <div class="photo-grid-label">${esc(p.jobDesc || '')}</div>
            <div class="photo-grid-date">${formatDate(p.addedAt)}</div>
          </div>`
        ).join('')}</div>`
      : `<div class="empty-state">
          <div class="empty-state-icon">\uD83D\uDCF7</div>
          <div class="empty-state-text">No photos yet</div>
          <div class="text-muted text-sm mt-sm">Capture photos at job sites</div>
        </div>`
    }
  `;
}

function mount() {
  // Job selector
  document.getElementById('camera-job-select')?.addEventListener('change', (e) => {
    _selectedJobId = e.target.value;
    rerender();
  });

  // Capture button — uses the hidden file input for native camera
  const fileInput = document.getElementById('camera-file-input');
  document.getElementById('capture-btn')?.addEventListener('click', () => {
    if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  });

  // Handle file selection
  fileInput?.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;

    // Read as data URL
    const reader = new FileReader();
    reader.onload = async () => {
      const rawBase64 = reader.result;
      toast('Processing photo...', 'info');

      try {
        const compressed = await compressPhoto(rawBase64);
        showPhotoPreview(compressed);
      } catch (e) {
        console.warn('[camera] Compress error:', e);
        showPhotoPreview(rawBase64);
      }
    };
    reader.readAsDataURL(file);
  });

  // Photo taps — view full size
  document.getElementById('recent-photos')?.addEventListener('click', (e) => {
    const item = e.target.closest('.photo-grid-item');
    if (!item) return;
    const url = item.dataset.photoUrl;
    const jobId = item.dataset.jobId;
    const job = jobId ? getJobById(jobId) : null;
    showModal('Photo', `
      <img src="${esc(url)}" style="width:100%;border-radius:8px;margin-bottom:8px;" />
      ${job ? `<div class="text-sm text-muted">${esc(job.desc || job.name || 'Job')} - ${esc(job.stage)}</div>` : ''}
    `, [
      { label: 'Close', onClick: closeModal },
    ]);
  });
}

function showPhotoPreview(base64) {
  const noteId = 'photo-note-' + Date.now();
  showModal('Photo Captured', `
    <img src="${base64}" style="width:100%;border-radius:8px;margin-bottom:12px;" />
    <div class="form-group">
      <label>Caption / Note (optional)</label>
      <input type="text" id="${noteId}" placeholder="Add a note..." />
    </div>
    ${!_selectedJobId ? '<div class="text-sm" style="color:var(--orange);">No job selected - select a job to attach this photo.</div>' : ''}
  `, [
    { label: 'Discard', onClick: closeModal },
    { label: 'Retake', onClick: () => {
      closeModal();
      const fileInput = document.getElementById('camera-file-input');
      if (fileInput) { fileInput.value = ''; fileInput.click(); }
    }},
    { label: _selectedJobId ? 'Attach to Job' : 'Save', type: 'gold', onClick: async () => {
      const note = document.getElementById(noteId)?.value?.trim() || '';

      if (_selectedJobId) {
        const url = await uploadPhoto(_selectedJobId, base64);
        if (url) {
          addJobPhoto(_selectedJobId, url);
          toast('Photo attached to job', 'success');
        } else {
          // Save locally with base64
          saveLocalPhoto(_selectedJobId, base64, note);
          toast('Photo saved locally (upload will retry)', 'warning');
        }
      } else {
        // Save to unattached photos
        saveLocalPhoto('', base64, note);
        toast('Photo saved locally', 'info');
      }
      closeModal();
      rerender();
    }},
  ]);
}

function saveLocalPhoto(jobId, base64, note) {
  if (jobId) {
    // Store on the job itself
    const job = getJobById(jobId);
    if (job) {
      addJobPhoto(jobId, base64);
    }
  }
  // Also store in local photo log
  const photos = load('local_photos', []);
  photos.unshift({
    id: Date.now().toString(36),
    jobId: jobId || '',
    url: base64,
    note: note || '',
    addedAt: new Date().toISOString(),
  });
  // Keep only last 50 local photos
  if (photos.length > 50) photos.length = 50;
  store('local_photos', photos);
}

function rerender() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {}

function getRecentPhotos() {
  const jobs = getJobs();
  const photos = [];
  for (const job of jobs) {
    if (!job.photos || !Array.isArray(job.photos)) continue;
    for (const photo of job.photos) {
      photos.push({
        url: typeof photo === 'string' ? photo : photo.url,
        jobId: job.id,
        jobDesc: job.desc || job.name || '',
        addedAt: typeof photo === 'object' ? photo.addedAt : job.updatedAt || '',
      });
    }
  }
  photos.sort((a, b) => (b.addedAt || '').localeCompare(a.addedAt || ''));
  return photos.slice(0, 20);
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) { return ''; }
}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default { render, mount, unmount };
